import axiosInstanceImporter from "../axiosInstanceImporter";

export const getEnrichmentPreviewErrorMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  if (typeof data?.title === "string" && data.title.trim()) return data.title;
  if (typeof error?.message === "string" && error.message.trim()) return error.message;
  return "Failed to preview enrichment rules.";
};

export const serializeEnrichmentRules = (rules = []) =>
  rules.map(({ fieldName, type, script }) => ({
    fieldName: fieldName || "",
    type,
    script: script || "",
  }));

export const formatEnrichmentPreviewValue = (value) => {
  if (value === null || value === undefined) return "(empty)";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

export const getEnrichmentPreviewResult = (preview, rule) => ({
  value: formatEnrichmentPreviewValue(preview?.[rule.fieldName]),
});

const resolveSourceFieldPath = (rule, product) => {
  // The output field may have a different name from the column read by the script.
  const references = (rule.script || "").matchAll(/\brawProduct((?:(?:\?\.|\.)[\w$]+|\[\s*(?:"[^"]+"|'[^']+'|\d+)\s*\])+)/g);
  for (const reference of references) {
    const segments = Array.from(reference[1].matchAll(/(?:\?\.|\.)([\w$]+)|\[\s*(?:"([^"]+)"|'([^']+)'|(\d+))\s*\]/g), (match) => match[1] ?? match[2] ?? match[3] ?? match[4]);
    const path = [];
    let cursor = product;
    for (const segment of segments) {
      if (!cursor || typeof cursor !== "object" || (Array.isArray(cursor) && segment === "length")) break;
      if (!Object.prototype.hasOwnProperty.call(cursor, segment)) break;
      path.push(segment);
      cursor = cursor[segment];
    }
    if (path.length) return path;
  }
  const fieldName = rule.fieldName || "";
  const key = Object.keys(product).find((name) => name.toLowerCase() === fieldName.toLowerCase());
  if (key) return [key];
  return fieldName ? fieldName.split(".") : undefined;
};

export const createEnrichmentRuleDetail = (rule, result, ruleIndex, rawProduct) => {
  const sourceFieldPath = rawProduct ? resolveSourceFieldPath(rule, rawProduct) : undefined;
  const value = sourceFieldPath?.reduce((owner, key) => owner?.[key], rawProduct);
  const sourceFieldType = value === undefined ? "string" : value === null || typeof value === "object" ? "json" : typeof value;
  const sourceFieldDraft = sourceFieldPath ? sourceFieldType === "json" ? JSON.stringify(value, null, 2) : String(value ?? "") : undefined;
  return { rule, result, ruleIndex, sourceFieldPath, sourceFieldType, sourceFieldDraft, originalSourceFieldDraft: sourceFieldDraft };
};

export const parseEnrichmentPreviewFieldValue = (draft, type) => {
  if (type === "number") {
    if (!draft.trim() || !Number.isFinite(Number(draft))) throw new Error("Enter a valid number.");
    return Number(draft);
  }
  if (type === "boolean") {
    if (!["true", "false"].includes(draft.trim().toLowerCase())) throw new Error("Enter true or false.");
    return draft.trim().toLowerCase() === "true";
  }
  if (type === "json") {
    try {
      return JSON.parse(draft);
    } catch {
      throw new Error("Enter valid JSON for this field's value.");
    }
  }
  return draft;
};

export const getEnrichmentPreviewSourceFieldEdit = (detail) => detail.sourceFieldPath ? {
  path: detail.sourceFieldPath,
  value: parseEnrichmentPreviewFieldValue(detail.sourceFieldDraft, detail.sourceFieldType),
} : undefined;

export const applyEnrichmentPreviewFieldEdit = (rawProduct, edit) => {
  const sample = JSON.parse(JSON.stringify(rawProduct));
  let owner = sample;
  edit.path.forEach((key, index) => {
    const last = index === edit.path.length - 1;
    if (last || !owner[key] || typeof owner[key] !== "object" || !Object.prototype.hasOwnProperty.call(owner, key)) {
      Object.defineProperty(owner, key, { value: last ? edit.value : {}, enumerable: true, configurable: true, writable: true });
    }
    owner = owner[key];
  });
  return sample;
};

const parseDelimitedFeed = (text, delimiter = ",") => {
  const rows = [];
  let row = [];
  let value = "";
  let quoted = false;
  const input = text.replace(/^\uFEFF/, "");

  for (let i = 0; i < input.length; i += 1) {
    const char = input[i];
    if (char === '"') {
      if (quoted && input[i + 1] === '"') {
        value += '"';
        i += 1;
      } else {
        quoted = !quoted;
      }
    } else if (!quoted && (char === delimiter || char === "\n" || char === "\r")) {
      row.push(value.trim());
      value = "";
      if (char !== delimiter) {
        if (row.some((cell) => cell !== "")) rows.push(row);
        row = [];
        if (char === "\r" && input[i + 1] === "\n") i += 1;
      }
    } else {
      value += char;
    }
  }
  if (quoted) throw new Error("Failed to parse feed: unterminated quoted value.");
  row.push(value.trim());
  if (row.some((cell) => cell !== "")) rows.push(row);
  const [headers = [], ...records] = rows;
  return records.map((record) => Object.fromEntries(headers.map((header, i) => [header, record[i] ?? ""])));
};

const getJsonProducts = (data) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  for (const key of ["products", "items", "data"]) {
    if (Array.isArray(data[key])) return data[key];
    if (data[key] && typeof data[key] === "object") return getJsonProducts(data[key]);
  }
  return [data];
};

const xmlElementToValue = (element) => {
  const children = Array.from(element.children);
  if (!children.length && !element.attributes.length) return element.textContent.trim();
  const value = Object.fromEntries(Array.from(element.attributes).map((attribute) => [attribute.name, attribute.value]));
  children.forEach((child) => {
    const key = child.localName;
    const childValue = xmlElementToValue(child);
    if (Object.prototype.hasOwnProperty.call(value, key)) {
      value[key] = Array.isArray(value[key]) ? [...value[key], childValue] : [value[key], childValue];
    } else {
      value[key] = childValue;
    }
  });
  if (!children.length) value.value = element.textContent.trim();
  return value;
};

const parseXmlProducts = (text) => {
  const document = new DOMParser().parseFromString(text, "application/xml");
  if (document.querySelector("parsererror")) throw new Error("Failed to parse XML feed.");
  const elements = Array.from(document.getElementsByTagName("*"));
  const products = elements.filter((element) => ["product", "item", "entry"].includes(element.localName.toLowerCase()));
  if (!products.length) throw new Error("No product found in feed");
  return products.map(xmlElementToValue);
};

const getProductField = (product, fieldName) => {
  if (Object.prototype.hasOwnProperty.call(product, fieldName)) return product[fieldName];
  const key = Object.keys(product).find((name) => name.toLowerCase() === fieldName.toLowerCase());
  if (key) return product[key];
  return fieldName.split(".").reduce((value, segment) => value?.[segment], product);
};

export const evaluateEnrichmentRules = (rawProduct, rules) => rules.map((rule) => {
  try {
    // Keep the downloaded sample and other rule tests independent of script mutations.
    const sample = JSON.parse(JSON.stringify(rawProduct));
    if (!rule.script?.trim()) return { value: formatEnrichmentPreviewValue(getProductField(sample, rule.fieldName)) };
    // eslint-disable-next-line no-new-func
    const evaluate = new Function("rawProduct", `return ${rule.script}`);
    return { value: formatEnrichmentPreviewValue(evaluate(sample)) };
  } catch (error) {
    return { error: error.message };
  }
});

const downloadPreviewFeed = async (tenantId, feedUrl) => {
  let response;
  try {
    response = await fetch(feedUrl);
  } catch (error) {
    // Fetch cannot distinguish a CORS rejection from other network failures.
    if (!(error instanceof TypeError)) throw error;
    const { data } = await axiosInstanceImporter.post("/Products/enrichment-preview-feed", { tenantId });
    if (typeof data?.content !== "string") throw new Error("The importer did not return feed content.");
    return data.content;
  }
  if (!response.ok) throw new Error(`Failed to fetch feed: ${response.status}`);
  return response.text();
};

const requestFileEnrichmentPreview = async (tenantId, rules, productId, { feedConfigurations, productIdFieldName = "id" }) => {
  const feedUrl = feedConfigurations?.dataFeedUrl;
  if (!feedUrl) throw new Error("No data feed URL configured");
  const extension = feedUrl.split(/[?#]/)[0].split(".").pop().toLowerCase();
  const dataType = (feedConfigurations.dataType || (["csv", "json", "xml", "txt"].includes(extension) ? extension : "csv")).trim().toLowerCase();
  if (!["csv", "json", "xml", "txt"].includes(dataType)) {
    throw new Error(`Rule test is not supported for ${dataType} feed type`);
  }
  const text = await downloadPreviewFeed(tenantId, feedUrl);
  let products;
  if (dataType === "json") {
    products = getJsonProducts(JSON.parse(text));
  } else if (dataType === "xml") {
    products = parseXmlProducts(text);
  } else {
    const delimiter = dataType === "txt" && text.split(/\r?\n/)[0].includes("\t") ? "\t" : ",";
    products = parseDelimitedFeed(text, delimiter);
  }
  const idField = productIdFieldName || "id";
  const rawProduct = (productId && products.find((product) =>
    String(getProductField(product, idField) ?? "") === productId
  )) || products[0];
  if (!rawProduct) throw new Error("No product found in feed");

  const results = evaluateEnrichmentRules(rawProduct, rules);
  return { preview: rawProduct, rawProduct, rules, results };
};

export const requestEnrichmentPreview = async (tenantId, rules, productId = "", options) => {
  const serializedRules = serializeEnrichmentRules(rules);
  const selectedProductId = String(productId ?? "").trim();
  if (options && options.feedConfigurations?.dataFeed?.trim().toLowerCase() !== "shopify") {
    return requestFileEnrichmentPreview(tenantId, serializedRules, selectedProductId, options);
  }
  const { data } = await axiosInstanceImporter.post("/Products/enrichment-preview", {
    tenantId,
    productId: selectedProductId,
    rules: serializedRules,
  });

  return {
    preview: data,
    rules: serializedRules,
    results: serializedRules.map((rule) => getEnrichmentPreviewResult(data, rule)),
  };
};
